import {
    Accordion,
    ActionIcon,
    Button,
    Group,
    NumberInput,
    Stack,
    Textarea,
    TextInput,
} from '@mantine/core';
import {
    CityQuestBonusSecretTexts,
    CityQuestEvidenceTexts,
    CityQuestTeamSplitRole,
} from '@routellers/types/dist/city-quest/city-quest-parts';
import { ImageTypeEnum } from '@routellers/types/dist/upload/enum/image-type.enum';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { MediaDropzone } from '../../components/editor';

/**
 * Необязательные поля точки, не зависящие от типа задания: «тогда/сейчас»,
 * видео, улика, бонус-секрет, роли команды, fallback. Свёрнуты в аккордеон,
 * чтобы не раздувать конструктор.
 */

/** В черновике acceptedAnswers — csv-строка, как у остальных полей ответа. */
export type BonusSecretDraft = Omit<CityQuestBonusSecretTexts, 'acceptedAnswers'> & {
    acceptedAnswers: string;
};

export type StopExtrasStopFields = {
    thenPhotoUrl: string | null;
    thenPhotoYear: string | null;
    videoUrl: string | null;
    videoPosterUrl: string | null;
    evidenceImageUrl: string | null;
    bonusSecretPoints: number | null;
};

export type StopExtrasLocaleFields = {
    thenPhotoCaption: string;
    evidence: CityQuestEvidenceTexts | null;
    bonusSecret: BonusSecretDraft | null;
    teamSplitRoles: CityQuestTeamSplitRole[];
    fallbackPrompt: string;
    fallbackAcceptedAnswers: string;
};

type Props = {
    stop: StopExtrasStopFields;
    locale: StopExtrasLocaleFields;
    onPatchStop: (patch: Partial<StopExtrasStopFields>) => void;
    onPatchLocale: (patch: Partial<StopExtrasLocaleFields>) => void;
};

export const StopExtrasAccordion = ({ stop, locale, onPatchStop, onPatchLocale }: Props) => {
    const { t } = useTranslation();
    const evidence = locale.evidence;
    const bonusSecret = locale.bonusSecret;

    const patchEvidence = (patch: Partial<CityQuestEvidenceTexts>) =>
        onPatchLocale({ evidence: { id: '', title: '', ...(evidence || {}), ...patch } });

    const patchBonusSecret = (patch: Partial<BonusSecretDraft>) =>
        onPatchLocale({
            bonusSecret: { prompt: '', acceptedAnswers: '', ...(bonusSecret || {}), ...patch },
        });

    const patchRole = (index: number, patch: Partial<CityQuestTeamSplitRole>) =>
        onPatchLocale({
            teamSplitRoles: locale.teamSplitRoles.map((role, i) =>
                i === index ? { ...role, ...patch } : role,
            ),
        });

    return (
        <Accordion variant='separated' multiple>
            <Accordion.Item value='thenPhoto'>
                <Accordion.Control>
                    {t('editors.quest.constructor.extras.thenPhoto.title')}
                </Accordion.Control>
                <Accordion.Panel>
                    <Stack gap='sm'>
                        <MediaDropzone
                            label={t('editors.quest.constructor.extras.thenPhoto.photo')}
                            kind='image'
                            imageType={ImageTypeEnum.CITY_READY_ROUTE}
                            value={stop.thenPhotoUrl}
                            onChange={(url) => onPatchStop({ thenPhotoUrl: url })}
                        />
                        <TextInput
                            label={t('editors.quest.constructor.extras.thenPhoto.year')}
                            value={stop.thenPhotoYear || ''}
                            onChange={(e) =>
                                onPatchStop({ thenPhotoYear: e.currentTarget.value.trim() || null })
                            }
                        />
                        <TextInput
                            label={t('editors.quest.constructor.extras.thenPhoto.caption')}
                            value={locale.thenPhotoCaption}
                            onChange={(e) =>
                                onPatchLocale({ thenPhotoCaption: e.currentTarget.value })
                            }
                        />
                    </Stack>
                </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value='video'>
                <Accordion.Control>
                    {t('editors.quest.constructor.extras.video.title')}
                </Accordion.Control>
                <Accordion.Panel>
                    <Stack gap='sm'>
                        <TextInput
                            label={t('editors.quest.constructor.extras.video.url')}
                            value={stop.videoUrl || ''}
                            onChange={(e) =>
                                onPatchStop({ videoUrl: e.currentTarget.value.trim() || null })
                            }
                        />
                        <TextInput
                            label={t('editors.quest.constructor.extras.video.posterUrl')}
                            value={stop.videoPosterUrl || ''}
                            onChange={(e) =>
                                onPatchStop({
                                    videoPosterUrl: e.currentTarget.value.trim() || null,
                                })
                            }
                        />
                    </Stack>
                </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value='evidence'>
                <Accordion.Control>
                    {t('editors.quest.constructor.extras.evidence.title')}
                </Accordion.Control>
                <Accordion.Panel>
                    <Stack gap='sm'>
                        <MediaDropzone
                            label={t('editors.quest.constructor.extras.evidence.image')}
                            kind='image'
                            imageType={ImageTypeEnum.CITY_READY_ROUTE}
                            value={stop.evidenceImageUrl}
                            onChange={(url) => onPatchStop({ evidenceImageUrl: url })}
                        />
                        <TextInput
                            label={t('editors.quest.constructor.extras.evidence.id')}
                            description={t('editors.quest.constructor.extras.evidence.idHint')}
                            value={evidence?.id || ''}
                            onChange={(e) => patchEvidence({ id: e.currentTarget.value })}
                        />
                        <TextInput
                            label={t('editors.quest.constructor.extras.evidence.evidenceTitle')}
                            value={evidence?.title || ''}
                            onChange={(e) => patchEvidence({ title: e.currentTarget.value })}
                        />
                        <Textarea
                            label={t('editors.quest.constructor.extras.evidence.description')}
                            minRows={2}
                            autosize
                            value={evidence?.description || ''}
                            onChange={(e) =>
                                patchEvidence({ description: e.currentTarget.value })
                            }
                        />
                    </Stack>
                </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value='bonusSecret'>
                <Accordion.Control>
                    {t('editors.quest.constructor.extras.bonusSecret.title')}
                </Accordion.Control>
                <Accordion.Panel>
                    <Stack gap='sm'>
                        <NumberInput
                            label={t('editors.quest.constructor.extras.bonusSecret.points')}
                            min={0}
                            value={stop.bonusSecretPoints ?? ''}
                            onChange={(v) =>
                                onPatchStop({
                                    bonusSecretPoints: typeof v === 'number' ? v : null,
                                })
                            }
                        />
                        <TextInput
                            label={t('editors.quest.constructor.extras.bonusSecret.teaser')}
                            description={t(
                                'editors.quest.constructor.extras.bonusSecret.teaserHint',
                            )}
                            value={bonusSecret?.teaser || ''}
                            onChange={(e) => patchBonusSecret({ teaser: e.currentTarget.value })}
                        />
                        <Textarea
                            label={t('editors.quest.constructor.extras.bonusSecret.prompt')}
                            minRows={2}
                            autosize
                            value={bonusSecret?.prompt || ''}
                            onChange={(e) => patchBonusSecret({ prompt: e.currentTarget.value })}
                        />
                        <TextInput
                            label={t(
                                'editors.quest.constructor.extras.bonusSecret.acceptedAnswers',
                            )}
                            value={bonusSecret?.acceptedAnswers || ''}
                            onChange={(e) =>
                                patchBonusSecret({ acceptedAnswers: e.currentTarget.value })
                            }
                        />
                        <TextInput
                            label={t('editors.quest.constructor.extras.bonusSecret.hint')}
                            value={bonusSecret?.hint || ''}
                            onChange={(e) => patchBonusSecret({ hint: e.currentTarget.value })}
                        />
                        <Textarea
                            label={t(
                                'editors.quest.constructor.extras.bonusSecret.solutionExplanation',
                            )}
                            minRows={2}
                            autosize
                            value={bonusSecret?.solutionExplanation || ''}
                            onChange={(e) =>
                                patchBonusSecret({ solutionExplanation: e.currentTarget.value })
                            }
                        />
                    </Stack>
                </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value='teamSplit'>
                <Accordion.Control>
                    {t('editors.quest.constructor.extras.teamSplit.title')}
                </Accordion.Control>
                <Accordion.Panel>
                    <Stack gap='xs'>
                        {locale.teamSplitRoles.map((role, index) => (
                            <Group key={index} align='flex-start' gap='xs' wrap='nowrap'>
                                <TextInput
                                    size='xs'
                                    label={t('editors.quest.constructor.extras.teamSplit.role')}
                                    value={role.role}
                                    onChange={(e) =>
                                        patchRole(index, { role: e.currentTarget.value })
                                    }
                                    style={{ width: 150 }}
                                />
                                <Textarea
                                    size='xs'
                                    label={t('editors.quest.constructor.extras.teamSplit.task')}
                                    minRows={2}
                                    autosize
                                    value={role.task}
                                    onChange={(e) =>
                                        patchRole(index, { task: e.currentTarget.value })
                                    }
                                    style={{ flex: 1 }}
                                />
                                <ActionIcon
                                    color='red'
                                    variant='subtle'
                                    mt={26}
                                    onClick={() =>
                                        onPatchLocale({
                                            teamSplitRoles: locale.teamSplitRoles.filter(
                                                (_, i) => i !== index,
                                            ),
                                        })
                                    }
                                >
                                    <IconTrash size={14} />
                                </ActionIcon>
                            </Group>
                        ))}
                        <Button
                            size='xs'
                            variant='light'
                            leftSection={<IconPlus size={14} />}
                            onClick={() =>
                                onPatchLocale({
                                    teamSplitRoles: [
                                        ...locale.teamSplitRoles,
                                        { role: '', task: '' },
                                    ],
                                })
                            }
                        >
                            {t('editors.quest.constructor.extras.teamSplit.addRole')}
                        </Button>
                    </Stack>
                </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value='fallback'>
                <Accordion.Control>
                    {t('editors.quest.constructor.extras.fallback.title')}
                </Accordion.Control>
                <Accordion.Panel>
                    <Stack gap='sm'>
                        <Textarea
                            label={t('editors.quest.constructor.extras.fallback.prompt')}
                            minRows={2}
                            autosize
                            value={locale.fallbackPrompt}
                            onChange={(e) =>
                                onPatchLocale({ fallbackPrompt: e.currentTarget.value })
                            }
                        />
                        <TextInput
                            label={t('editors.quest.constructor.extras.fallback.acceptedAnswers')}
                            value={locale.fallbackAcceptedAnswers}
                            onChange={(e) =>
                                onPatchLocale({ fallbackAcceptedAnswers: e.currentTarget.value })
                            }
                        />
                    </Stack>
                </Accordion.Panel>
            </Accordion.Item>
        </Accordion>
    );
};
